import { supabase } from "@/lib/supabase";
import { buildDocumentDemandeLink } from "@/lib/admin/pending-demandes";

/**
 * Admin notification types - specific to admin alerts
 */
export type AdminNotificationType =
  | "admin_vehicle_pending"
  | "admin_document_pending"
  | "admin_cancellation_pending"
  | "admin_renter_signup";

/**
 * All admin notification types as array for filtering
 */
export const ADMIN_NOTIFICATION_TYPES: AdminNotificationType[] = [
  "admin_vehicle_pending",
  "admin_document_pending",
  "admin_cancellation_pending",
  "admin_renter_signup",
];

/**
 * Admin notification category for UI grouping
 */
export type AdminNotificationCategory = "vehicles" | "documents" | "cancellations" | "all";

/**
 * Raw admin notification from database
 */
export interface AdminNotification {
  id: string;
  user_id: string;
  type: AdminNotificationType;
  title: string;
  message: string;
  is_read: boolean;
  related_id: string | null;
  created_at: string;
  updated_at?: string;
}

/**
 * Formatted admin notification for UI display
 */
export interface FormattedAdminNotification {
  id: string;
  type: AdminNotificationType;
  category: AdminNotificationCategory;
  title: string;
  message: string;
  isRead: boolean;
  relatedId: string | null;
  createdAt: string;
  timeAgo: string;
  actionLink: string;
  actionText: string;
}

/**
 * Admin notification counts by category
 */
export interface AdminNotificationCounts {
  total: number;
  vehicles: number;
  documents: number;
  cancellations: number;
}

/**
 * Map notification type to category
 */
function getCategory(type: AdminNotificationType): AdminNotificationCategory {
  switch (type) {
    case "admin_vehicle_pending":
      return "vehicles";
    case "admin_document_pending":
      return "documents";
    case "admin_cancellation_pending":
      return "cancellations";
    case "admin_renter_signup":
      return "all";
    default:
      return "all";
  }
}

/**
 * Get action link for notification type
 */
export function getActionLink(type: AdminNotificationType, relatedId: string | null): string {
  switch (type) {
    case "admin_vehicle_pending":
      return "/admin/vehicles";
    case "admin_document_pending":
      return buildDocumentDemandeLink(relatedId);
    case "admin_cancellation_pending":
      return "/admin/cancellations";
    case "admin_renter_signup":
      return "/admin/users";
    default:
      return "/admin";
  }
}

/**
 * Get action text for notification type
 */
function getActionText(type: AdminNotificationType): string {
  switch (type) {
    case "admin_vehicle_pending":
      return "Voir les véhicules";
    case "admin_document_pending":
      return "Voir les documents";
    case "admin_cancellation_pending":
      return "Voir les annulations";
    case "admin_renter_signup":
      return "Voir les utilisateurs";
    default:
      return "Voir";
  }
}

/**
 * Format time ago string
 */
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "À l'instant";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `Il y a ${minutes} min`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `Il y a ${hours}h`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `Il y a ${days}j`;
  } else {
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    });
  }
}

/**
 * Format raw notification to UI-friendly format
 */
function formatNotification(notification: AdminNotification): FormattedAdminNotification {
  const category = getCategory(notification.type);
  return {
    id: notification.id,
    type: notification.type,
    category,
    title: notification.title,
    message: notification.message,
    isRead: notification.is_read,
    relatedId: notification.related_id,
    createdAt: notification.created_at,
    timeAgo: formatTimeAgo(notification.created_at),
    actionLink: getActionLink(notification.type, notification.related_id),
    actionText: getActionText(notification.type),
  };
}

/**
 * AdminNotificationService - Handles admin-specific notification operations
 * KISS: Single responsibility - only manages admin notifications
 * SOLID: SRP - focused on admin notification operations only
 */
export const adminNotificationService = {
  /**
   * Verify current user is admin
   */
  async verifyAdminAccess(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Non authentifié");

    const { data: profile, error } = await supabase
      .from("profiles")
      .select("role")
      .eq("id", user.id)
      .single();

    if (error || !profile || profile.role !== "admin") {
      throw new Error("Accès admin requis");
    }

    return user.id;
  },

  /**
   * Get admin notifications with optional filters
   */
  async getAdminNotifications(
    options: {
      unreadOnly?: boolean;
      category?: AdminNotificationCategory;
      limit?: number;
    } = {}
  ): Promise<FormattedAdminNotification[]> {
    const userId = await this.verifyAdminAccess();
    const { unreadOnly = false, category = "all", limit = 50 } = options;

    console.log("[AdminNotificationService] getAdminNotifications called", {
      userId,
      unreadOnly,
      category,
      limit
    });

    let query = supabase
      .from("notifications")
      .select("id, user_id, type, title, message, is_read, related_id, created_at")
      .eq("user_id", userId);

    if (category === "all") {
      // Filter by ALL admin types
      console.log("[AdminNotificationService] Filtering by admin types:", ADMIN_NOTIFICATION_TYPES);
      query = query.in("type", ADMIN_NOTIFICATION_TYPES);
    } else {
      const typeMap: Record<Exclude<AdminNotificationCategory, "all">, AdminNotificationType> = {
        vehicles: "admin_vehicle_pending",
        documents: "admin_document_pending",
        cancellations: "admin_cancellation_pending",
      };
      const type = typeMap[category];
      if (type) {
        console.log("[AdminNotificationService] Filtering by type:", type);
        query = query.eq("type", type);
      }
    }

    if (unreadOnly) {
      query = query.eq("is_read", false);
    }

    query = query.order("created_at", { ascending: false }).limit(limit);

    const { data, error } = await query;

    if (error) {
      console.error("[AdminNotificationService] Error fetching notifications:", error);
      throw new Error(error.message);
    }

    console.log("[AdminNotificationService] Raw data received:", data);
    const formatted = (data || []).map((n) => formatNotification(n as AdminNotification));
    console.log("[AdminNotificationService] Formatted notifications:", formatted);

    return formatted;
  },

  /**
   * Get unread count by category
   */
  async getUnreadCounts(): Promise<AdminNotificationCounts> {
    const userId = await this.verifyAdminAccess();

    const { data, error } = await supabase
      .from("notifications")
      .select("type")
      .eq("user_id", userId)
      .eq("is_read", false)
      .in("type", ADMIN_NOTIFICATION_TYPES);

    if (error) {
      console.error("[AdminNotificationService] Error fetching counts:", error);
      throw new Error(error.message);
    }

    const counts: AdminNotificationCounts = {
      total: 0,
      vehicles: 0,
      documents: 0,
      cancellations: 0,
    };

    (data || []).forEach((n) => {
      counts.total++;
      switch (n.type) {
        case "admin_vehicle_pending":
          counts.vehicles++;
          break;
        case "admin_document_pending":
          counts.documents++;
          break;
        case "admin_cancellation_pending":
          counts.cancellations++;
          break;
      }
    });

    return counts;
  },

  /**
   * Get total unread count
   */
  async getUnreadCount(): Promise<number> {
    const userId = await this.verifyAdminAccess();

    const { count, error } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("is_read", false)
      .in("type", ADMIN_NOTIFICATION_TYPES);

    if (error) {
      console.error("[AdminNotificationService] Error fetching unread count:", error);
      throw new Error(error.message);
    }

    return count || 0;
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    const userId = await this.verifyAdminAccess();

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId)
      .eq("user_id", userId);

    if (error) {
      console.error("[AdminNotificationService] Error marking as read:", error);
      throw new Error(error.message);
    }
  },

  /**
   * Mark all admin notifications as read
   */
  async markAllAsRead(): Promise<void> {
    const userId = await this.verifyAdminAccess();

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", userId)
      .eq("is_read", false)
      .in("type", ADMIN_NOTIFICATION_TYPES);

    if (error) {
      console.error("[AdminNotificationService] Error marking all as read:", error);
      throw new Error(error.message);
    }
  },

  /**
   * Mark all notifications of a specific category as read
   */
  async markCategoryAsRead(category: AdminNotificationCategory): Promise<void> {
    if (category === "all") {
      return this.markAllAsRead();
    }

    const userId = await this.verifyAdminAccess();

    const typeMap: Record<AdminNotificationCategory, AdminNotificationType | null> = {
      vehicles: "admin_vehicle_pending",
      documents: "admin_document_pending",
      cancellations: "admin_cancellation_pending",
      all: null,
    };

    const type = typeMap[category];
    if (!type) return;

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", userId)
      .eq("is_read", false)
      .eq("type", type);

    if (error) {
      console.error("[AdminNotificationService] Error marking category as read:", error);
      throw new Error(error.message);
    }
  },

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    const userId = await this.verifyAdminAccess();

    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId)
      .eq("user_id", userId);

    if (error) {
      console.error("[AdminNotificationService] Error deleting notification:", error);
      throw new Error(error.message);
    }
  },
};

